import { Box, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { translate } from "../../translate/translate";
import { Value } from "./value-row-column.component";
import dayjs from "dayjs";
import { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";

interface Column {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  minWidth?: number;
}

interface DraggableTableProps<T> {
  columns: Column[];
  rows: T[];
  rowKey: keyof T;
  onReorder?: (newRows: T[]) => void;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  headerCellCustomStyle?: (column?: string) => any;
  height?: number | string;
  stickyHeader?: boolean;
  renderCellHandler?: any;
  renderHeaderCellHandler?: any;
  headerCheckbox?: {
    allSelected: boolean;
    onSelectAll: (checked: boolean) => void;
  };
  isRowDraggable?: any;
  isArrowsDisabled?: any;
  dateColumns?: any;
}

interface SortableRowProps<T> {
  row: T;
  rowKey: keyof T;
  columns: Column[];
  index: number;
  isDraggable: boolean;
  renderCellHandler?: any;
  dateColumns?: any;
  onMoveUp?: (index: number) => void;
  onMoveDown?: (index: number) => void;
  isArrowsDisabled?: any;
  totalRows: number;
}

// Componente sortável para cada linha
function SortableRow<T extends { [key: string]: any }>({
  row,
  rowKey,
  columns,
  index,
  isDraggable,
  renderCellHandler,
  dateColumns,
  onMoveUp,
  onMoveDown,
  isArrowsDisabled,
  totalRows,
}: SortableRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: String(row[rowKey]),
    disabled: isDraggable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const translateRowColumn = (column: string, indexI: number): any => {
    if (isDate(column)) {
      return <TableCell key={indexI}>{dateFormat(column)}</TableCell>;
    }
    const value = String(Value(row, column));
    return <TableCell key={indexI}>{translate.t(value)}</TableCell>;
  };

  const dateFormat = (column: string) => {
    if (!String(Value(row, column))) return "-";

    const cleanedDateTime = String(Value(row, column)).replace(/\.\d+/, "");

    const timeFormat = translate.language === "pt" ? "HH:mm" : "hh:mm A";

    const formattedDate = dayjs(cleanedDateTime).format("L");
    const formattedTime = dayjs(cleanedDateTime).format(timeFormat);

    return `${formattedDate} ${formattedTime}`;
  };

  const isDate = (column: string): boolean => {
    return dateColumns === undefined ? false : dateColumns.includes(column);
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      sx={{
        background: isDragging ? "#cce5ff" : "inherit",
        cursor: isDraggable ? "not-allowed" : "grab",
        transition: "background 0.2s ease",
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      {columns.map((col: any, indexI: any) =>
        renderCellHandler && renderCellHandler[col?.field]
          ? renderCellHandler[col?.field](col?.field, indexI, row)
          : translateRowColumn(col?.field, indexI)
      )}

      <TableCell align="center" sx={{ width: 120 }}>
        <IconButton
          size="small"
          onClick={() => onMoveUp && onMoveUp(index)}
          disabled={index === 0 || isArrowsDisabled(row)}
        >
          <KeyboardArrowUp fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onMoveDown && onMoveDown(index)}
          disabled={index === totalRows - 1 || isArrowsDisabled(row)}
        >
          <KeyboardArrowDown fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

function DraggableTable<T extends { [key: string]: any }>({
                                                            columns,
                                                            rows,
                                                            rowKey,
                                                            onReorder,
                                                            onMoveUp,
                                                            onMoveDown,
                                                            renderCellHandler,
                                                            renderHeaderCellHandler,
                                                            headerCellCustomStyle,
                                                            height = 400,
                                                            stickyHeader = false,
                                                            dateColumns,
                                                            isRowDraggable,
                                                            isArrowsDisabled,
                                                          }: DraggableTableProps<T>) {
  const [, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id) {
      const oldIndex = rows.findIndex(
        (row) => String(row[rowKey]) === String(active.id)
      );
      const newIndex = rows.findIndex(
        (row) => String(row[rowKey]) === String(over.id)
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        const newRows = arrayMove(rows, oldIndex, newIndex);
        if (onReorder) {
          onReorder(newRows);
        }
      }
    }
  };

  const baseStyle = {
    lineHeight: "48px",
    padding: "0 14px",
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#3949AB",
    paddingTop: "15px",
    paddingBottom: "15px",
    width: "100%",
  };

  const headerCellCustomStyleHandler = (column?: string) => {
    if (headerCellCustomStyle) {
      return headerCellCustomStyle(column);
    }
    return baseStyle;
  };

  const items = rows.map((row) => String(row[rowKey]));

  return (
    <Box sx={{ position: "relative" }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={(event) => setActiveId(String(event.active.id))}
      >
        <TableContainer
          component={Paper}
          id="draggable-table"
          sx={{
            height: rows.length > 0 ? height : "inherit",
            width: "100%",
            position: "relative",
          }}
        >
          <Table stickyHeader={stickyHeader}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      width: col.width,
                      height: 48,
                      ...headerCellCustomStyleHandler(col.field),
                    }}
                  >
                    {renderHeaderCellHandler && renderHeaderCellHandler[col.field]
                      ? renderHeaderCellHandler[col.field](col.field)
                      : translate.t(col.headerName)}
                  </TableCell>
                ))}
                <TableCell
                  sx={{
                    width: 120,
                    textAlign: "center",
                    ...headerCellCustomStyleHandler(),
                  }}
                >
                  {translate.t("actions")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.length > 0 ? (
                <SortableContext
                  items={items}
                  strategy={verticalListSortingStrategy}
                >
                  {rows.map((row, index) => (
                    <SortableRow
                      key={String(row[rowKey])}
                      row={row}
                      rowKey={rowKey}
                      columns={columns}
                      index={index}
                      isDraggable={isRowDraggable(row)}
                      renderCellHandler={renderCellHandler}
                      dateColumns={dateColumns}
                      onMoveUp={onMoveUp}
                      onMoveDown={onMoveDown}
                      isArrowsDisabled={isArrowsDisabled}
                      totalRows={rows.length}
                    />
                  ))}
                </SortableContext>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + 1}
                    sx={{ textAlign: "center" }}
                  >
                    {translate.t("empty_listing")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DndContext>
    </Box>
  );
}

export default DraggableTable;
